<template>
  <b-container fluid>
    <template v-if="coordinatorsLoaded">
      <b-select
        v-if="isAdmin || isSRC"
        v-model="RCUuid"
        @change="
          resetFilters();
          switchRC();
        "
        class="mb-4"
      >
        <b-select-option value=""
          >Choose Regional Coordinator...</b-select-option
        >
        <b-select-option-group
          v-for="(country, label) in coordinators"
          :label="label"
          :key="label"
        >
          <b-select-option
            v-for="coordinator in country"
            :value="coordinator.uuid"
            :key="coordinator.uuid"
            >{{
              `${coordinator.first_name} ${coordinator.last_name}`
            }}</b-select-option
          >
        </b-select-option-group>
      </b-select>
      <template v-if="rcMeta && dataLoaded">
        <b-container fluid>
          <b-row>
            <b-col class="pl-0">
              <h1 class="mb-1">{{ `${rcMeta.first_name} ${rcMeta.last_name}` }}</h1>
              <p class="text-primary-dark small mb-2">
                Regional Coordinator, {{ rcMeta.district }}, {{ rcMeta.region }}, {{ rcMeta.country }}<br>
                in last 12 months: {{ RCTrainerActivityData.counts.teams }} team{{ RCTrainerActivityData.counts.teams > 1 ? 's' : '' }}, {{ RCTrainerActivityData.counts.trainers }} trainers, {{ RCTrainerActivityData.counts.groups }} groups, {{ RCTrainerActivityData.counts.teachers }} teachers, {{ RCTrainerActivityData.counts.schools }} schools<br>
              </p>
              <p>
                <b-button
                  size="sm"
                  class="bg-light border-light text-danger mr-2"
                  v-b-modal.data_warnings
                  >data warnings</b-button
                >
                <b-button
                  size="sm"
                  class="bg-light border-light text-danger mr-2"
                  v-b-modal.location_warnings
                  >location warnings</b-button
                >
                <b-button
                  size="sm"
                  class="bg-light border-light text-danger mr-2"
                  v-b-modal.missing_schools_warnings
                  >teachers missing schools</b-button
                >
              </p>
            </b-col>
            <b-col class="pr-0">
              <b-table-simple borderless outlined small class="small">
                <b-tr
                  ><b-td colspan="2"
                    ><h3 class="text-secondary-mid">LEGEND</h3></b-td
                  ></b-tr
                >
                <b-tr
                  ><b-td class="text-right"
                    ><span class="text-monospace">Average PPI</span></b-td
                  ><b-td
                    >average of the three most recent class visits</b-td
                  ></b-tr
                >
                <b-tr
                  ><b-td class="text-right"
                    ><b-badge variant="warning">date</b-badge></b-td
                  ><b-td
                    >teachers who have not been visited in the last 3 months
                    (module) / 6 months (maintenance)</b-td
                  ></b-tr
                >
                <b-tr
                  ><b-td class="text-right"
                    ><b-badge variant="warning" class="warning-light">date</b-badge></b-td
                  ><b-td
                    >visit that would normally be overdue but teacher left the school or the programme</b-td
                  ></b-tr
                >
                <b-tr
                  ><b-td class="text-right"
                    ><b-badge class="bg-grey-mid text-white">mo</b-badge> /
                    <b-badge class="bg-grey-mid text-white">ma</b-badge></b-td
                  ><b-td>visits whose type don't match their group</b-td></b-tr
                >
                <b-tr
                  ><b-td class="text-right"
                    ><b-badge variant="secondary">RC</b-badge></b-td
                  ><b-td
                    >where a Regional Coordinator accompanied a Trainer on class
                    visit</b-td
                  ></b-tr
                >
              </b-table-simple>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="pl-0">
              <h2 class="text-primary-dark">Recent activity by all trainers</h2>
            </b-col>
            <b-col class="text-right pr-0">
              <b-button class="ml-2 bg-light text-dark" v-b-modal.map
                >view results on map</b-button
              >
              <b-button
                class="ml-2 bg-secondary-mid"
                :disabled="
                  JSON.stringify(currentFilters) ==
                    JSON.stringify(initialFilters) &&
                    currentSortBy == initialSortBy &&
                    currentSortDesc == initialSortDesc
                "
                v-on:click="resetFilters()"
                >reset filters</b-button
              >
            </b-col>
          </b-row>
        </b-container>
        <b-table striped borderless outlined sort-icon-left small class="small" sticky-header="90vh" thead-tr-class="bg-secondary-mid text-white" :sort-by.sync="currentSortBy" :sort-desc.sync="currentSortDesc" :fields="RCTrainerActivityData.cols" :items="filteredItems" @sort-changed="sortTable">
          <template #head()="data">
            {{ data.label }}
            <b-select
              size="sm"
              v-if="
                ['trainer_name', 'group_name', 'group_type'].includes(
                  data.field.key
                )
              "
              v-model="currentFilters[data.field.key]"
            >
              <b-select-option value="">show all...</b-select-option>
              <b-select-option
                v-for="value in availableChoices[data.field.key]"
                :key="value"
                :value="value"
                >{{ value }}</b-select-option
              >
            </b-select>
            <b-form-input
              v-if="['teacher_name', 'school_name'].includes(data.field.key)"
              size="sm"
              v-model="currentFilters[data.field.key]"
              placeholder="search"
            ></b-form-input>
            <b-badge
              v-if="data.field.key == 'last_visit_date' && lastVisitWarningCount > 0"
              variant="warning"
              class="h5 mb-0"
              >{{ lastVisitWarningCount }}</b-badge
            >
          </template>
          <template #cell(trainer_name)="data">
            <template v-if="currentFilters.trainer_name === '' || (currentFilters.trainer_name && data.index == 0)">
              <a v-if="isAdmin || isSRC" :href="'/trainers/' + data.item.trainer_uuid">{{ data.value }}</a>
              <span v-else>{{ data.value }}</span>
            </template>
          </template>
          <template #cell(group_name)="data">
            <template v-if="currentFilters.group_name === '' || (currentFilters.group_name && data.index == 0)">
              <a v-if="isAdmin || isSRC" :href="'/groups/' + data.item.group_uuid">{{ data.value }}</a>
              <span v-else>{{ data.value }}</span>
            </template>
          </template>
          <template #cell(teacher_name)="data">
            <a v-if="isAdmin || isSRC" :href="'/teachers/' + data.item.teacher_uuid">{{ data.value }}</a>
            <span v-else>{{ data.value }}</span>
          </template>
          <template #cell(school_name)="data">
            <a v-if="isAdmin || isSRC" :href="'/schools/' + data.item.school_uuid">{{ data.value }}</a>
            <span v-else>{{ data.value }}</span>
          </template>
          <template #cell(last_visit_date)="data">
            <b-badge
              :class="`h5 mb-0${data.item.last_visit_warning == 'inactive_overdue' ? ' warning-light' : ''}`"
              :variant="data.item.last_visit_warning ? 'warning' : 'light'"
            >
              {{ formatDate(data.value) }}
            </b-badge>
            <b-badge
              class="bg-grey-mid text-white h5 mb-0 ml-1"
              v-if="data.item.last_visit_type != data.item.group_type"
            >
              {{ data.item.last_visit_type.substr(0, 2) }}
            </b-badge>
            <b-badge
              variant="secondary"
              class="h5 mb-0 ml-1"
              v-if="data.item.last_visit_coordinator_present"
            >
              RC
            </b-badge>
          </template>
        </b-table>
        <b-modal
          size="xl"
          id="data_warnings"
          title="Data warnings"
          header-border-variant="danger"
          footer-border-variant="danger"
          ok-variant="danger"
          ok-only
        >
          <template
            v-if="
              Object.keys(RCTrainerActivityData.data_warnings).reduce(
                (result, category) =>
                  result ||
                  RCTrainerActivityData.data_warnings[category].data.length > 0,
                false
              )
            "
          >
            <template
              v-for="(data, type) in RCTrainerActivityData.data_warnings"
            >
              <template
                v-if="RCTrainerActivityData.data_warnings[type].data.length"
              >
                <h4>
                  {{ RCTrainerActivityData.data_warnings[type].label }} ({{
                    RCTrainerActivityData.data_warnings[type].data.length
                  }})
                </h4>
                <ul class="text-monospace small">
                  <li
                    v-for="warning in RCTrainerActivityData.data_warnings[type]
                      .data"
                    :key="warning"
                  >
                    {{ warning }}
                  </li>
                </ul>
              </template>
            </template>
          </template>
          <template v-else>
            No data warnings for {{ rcMeta.first_name }}
            {{ rcMeta.last_name }} in the last 12 months
          </template>
        </b-modal>
        <b-modal
          size="xl"
          id="location_warnings"
          title="Location warnings"
          header-border-variant="danger"
          footer-border-variant="danger"
          ok-variant="danger"
          ok-only
        >
          <template
            v-if="
              Object.keys(RCTrainerActivityData.location_warnings).reduce(
                (result, category) =>
                  result ||
                  RCTrainerActivityData.location_warnings[category].data
                    .length > 0,
                false
              )
            "
          >
            <template
              v-for="(data, type) in RCTrainerActivityData.location_warnings"
            >
              <template
                v-if="RCTrainerActivityData.location_warnings[type].data.length"
              >
                <h4>
                  {{ RCTrainerActivityData.location_warnings[type].label }} ({{
                    RCTrainerActivityData.location_warnings[type].data.length
                  }})
                </h4>
                <ul class="text-monospace small">
                  <li
                    v-for="warning in RCTrainerActivityData.location_warnings[
                      type
                    ].data"
                    :key="warning"
                  >
                    {{ warning }}
                  </li>
                </ul>
              </template>
            </template>
          </template>
          <template v-else>
            No location warnings for {{ rcMeta.first_name }}
            {{ rcMeta.last_name }} in the last 12 months
          </template>
        </b-modal>
        <b-modal
          size="xl"
          id="missing_schools_warnings"
          title="Teachers missing schools warnings"
          header-border-variant="danger"
          footer-border-variant="danger"
          ok-variant="danger"
          ok-only
        >
          <template
            v-if="Object.keys(RCTrainerActivityData.missing_schools_warnings).some(
              trainer => Object.keys(RCTrainerActivityData.missing_schools_warnings[trainer].teachers).length > 0
            )"
          >
            <template
              v-for="(data, trainer) in RCTrainerActivityData.missing_schools_warnings"
            >
              <template
                v-if="Object.keys(RCTrainerActivityData.missing_schools_warnings[trainer].teachers).length"
              >
                <h4>
                  {{ RCTrainerActivityData.missing_schools_warnings[trainer].label }} ({{
                    Object.keys(RCTrainerActivityData.missing_schools_warnings[trainer].teachers).length
                  }})
                </h4>
                <ul class="text-monospace small">
                  <li
                    v-for="warning in RCTrainerActivityData.missing_schools_warnings[trainer].teachers"
                    :key="warning"
                  >
                    {{ warning.label }} ({{ warning.obsCount }} observations) <b v-if="warning.missingConsent">data consent not found</b>
                  </li>
                </ul>
              </template>
            </template>
          </template>
          <template v-else>
            No teachers missing schools for {{ rcMeta.first_name }}
            {{ rcMeta.last_name }} in the last 12 months
          </template>
        </b-modal>
        <b-modal
          id="map"
          title="Map of results"
          ok-only
          size="xl"
          @shown="renderMap"
        >
          <div class="trainer-activity-map" id="trainer-activity-map"></div>
        </b-modal>
      </template>
      <h2 v-else-if="RCUuid && !dataLoaded" class="loading-placeholder">Loading...</h2>
      <div v-else-if="errorLoading" class="alert alert-warning" role="alert">Error ocurred while loading data</div>
    </template>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import MIOService from "../services/MIOService";
import { calculateHaversine, isDateMonthsAgo } from "@/utils/MioHelpers";
export default {
  name: "RCTrainerActivity",
  data() {
    return {
      coordinatorsLoaded: false,
      dataLoaded: false,
      errorLoading: false,
      urlParams: new URLSearchParams(window.location.search),
      RCUuid: "",
      rcActiveTeamTrainers: [],
      rcActiveTeamTrainerGroups: [],
      rcActiveTrainerRecentObservations: [],
      rcActiveTrainerTeacherModules: [],
      rcMeta: [],
      initialFilters: {
        trainer_name: "",
        group_name: "",
        group_type: "",
        teacher_name: "",
        school_name: ""
      },
      currentFilters: {},
      initialSortBy: "last_visit_date",
      currentSortBy: "",
      initialSortDesc: false,
      currentSortDesc: "",
      filterTypes: {
        partial: ["teacher_name", "school_name"]
      },
      availableChoices: {},
      lastVisitWarningCount: 0
      // getCoordinatorUuid: "b46dfd78-3572-41bb-b846-9fc2f9283c73", // for testing RC own login
    };
  },
  created() {
    this.getPeople();
    if (this.getCoordinatorUuid) {
      this.RCUuid = this.getCoordinatorUuid;
    } else if (this.urlParams.get("coord_id") !== null) {
      this.RCUuid = this.urlParams.get("coord_id");
    } else {
      this.resetFilters();
    }
    if (this.RCUuid) {
      this.loadFilters();
      this.switchRC();
    }
    this.$loadScript("https://unpkg.com/leaflet@1.7.1/dist/leaflet.js");
  },
  methods: {
    setUrlParam(param, value) {
      this.urlParams.set(param, value);
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${this.urlParams}`
      );
    },
    deleteUrlParam(param) {
      this.urlParams.delete(param);
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${this.urlParams}`
      );
    },
    formatDate(date) {
      let options = { month: "short", day: "numeric" };
      return date.toLocaleString("default", options);
    },
    loadFilters() {
      this.currentFilters = { ...this.initialFilters };
      Object.keys(this.currentFilters).forEach(field => {
        let value = this.urlParams.get(field);
        if (value !== null) {
          this.currentFilters[field] = value;
        }
      });
      if (
        this.urlParams.get("sort_by") &&
        this.urlParams.get("sort_by") != this.initialSortBy
      ) {
        this.currentSortBy = this.urlParams.get("sort_by");
      } else {
        this.currentSortBy = this.initialSortBy;
      }
      if (
        this.urlParams.get("sort_desc") &&
        this.urlParams.get("sort_desc") != this.initialSortDesc
      ) {
        this.currentSortDesc = this.urlParams.get("sort_desc");
      } else {
        this.currentSortDesc = this.initialSortDesc;
      }
    },
    resetFilters() {
      this.currentFilters = { ...this.initialFilters };
      Object.keys(this.currentFilters).forEach(field => {
        if (this.initialFilters[field] == this.currentFilters[field]) {
          this.deleteUrlParam(field);
        }
      });
      this.deleteUrlParam("sort_by");
      this.currentSortBy = this.initialSortBy;
      this.deleteUrlParam("sort_desc");
      this.currentSortDesc = this.initialSortDesc;
    },
    sortTable(context) {
      this.currentSortBy = context.sortBy;
      if (context.sortBy == this.initialSortBy) {
        this.deleteUrlParam("sort_by");
      } else {
        this.setUrlParam("sort_by", context.sortBy);
      }
      this.currentSortDesc = context.sortDesc;
      if (context.sortDesc == this.initialSortDesc) {
        this.deleteUrlParam("sort_desc");
      } else {
        this.setUrlParam("sort_desc", context.sortDesc);
      }
    },
    renderMap() {
      if (!L) {
        return false;
      }
      let mapElement = document.getElementById("trainer-activity-map");
      if (mapElement) {
        mapElement.innerHTML = "";
      }
      let map = L.map("trainer-activity-map").setView(
        [-16.9465096, 27.2504284],
        5
      );
      let tileLayer = L.tileLayer(
        "https://cartodb-basemaps-{s}.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}.png",
        {
          maxZoom: 18
        }
      );
      tileLayer.addTo(map);
      let layer = L.layerGroup();
      this.filteredItems.forEach(obs => {
        if (!obs.last_visit_lat || !obs.last_visit_lng) {
          return;
        }
        let popupContent = `<strong>${this.formatDate(
          obs.last_visit_date
        )}</strong> <br> Last visit by trainer <strong>${
          obs.trainer_name
        }</strong> <br>to <strong>${
          obs.teacher_name
        }</strong>'s classroom <br>at <strong>${obs.school_name}</strong> `;
        let marker = L.marker([obs.last_visit_lat, obs.last_visit_lng], {
          title: obs.school_name
        })
          .bindPopup(popupContent)
          .addTo(layer);
        marker.addTo(layer);
      });
      layer.addTo(map);
      let bounds = layer.getLayers().map(loc => loc.getLatLng());
      if (bounds.length > 0) {
        map.fitBounds(bounds);
      }
    },
    async switchRC() {
      this.dataLoaded = false;
      this.errorLoading = false;
      if (!this.RCUuid) {
        this.deleteUrlParam("coord_id");
        return;
      }
      await this.getRCMeta(this.RCUuid);
      this.getRCData(this.RCUuid);
      if (this.getCoordinatorUuid) {
        this.deleteUrlParam("coord_id");
      } else {
        this.setUrlParam("coord_id", this.RCUuid);
      }
    },
    ...mapActions({
      GET_COORDINATORS: "coordinators/GET_COORDINATORS",
      GET_GROUPS: "groups/GET_GROUPS"
    }),
    async getPeople() {
      try {
        await this.GET_COORDINATORS();
        this.coordinatorsLoaded = true;
      } catch (err) {
        console.error(err);
      }
    },
    async getRCData(coordinatorUuid) {
      try {
        const [rcActiveTeamTrainers, rcActiveTeamTrainerGroups, rcActiveTrainerRecentObservations, rcActiveTrainerTeacherModules, dataConsenters] = await Promise.all([
          MIOService.getRCActiveTeamTrainers(coordinatorUuid, '1 year'),
          MIOService.getRCActiveTeamTrainerGroups(coordinatorUuid),
          MIOService.getRCActiveTrainerRecentObservations(coordinatorUuid),
          MIOService.getRCActiveTrainerTeacherModules(coordinatorUuid),
          MIOService.getDataConsentersCountry(this.rcMeta.country),
        ]);
        this.rcActiveTeamTrainers = rcActiveTeamTrainers.data.results;
        this.rcActiveTeamTrainerGroups = rcActiveTeamTrainerGroups.data.results;
        this.rcActiveTrainerRecentObservations = rcActiveTrainerRecentObservations.data.results;
        this.rcActiveTrainerTeacherModules = rcActiveTrainerTeacherModules.data.results;
        this.dataConsenters = dataConsenters.data.results;
        this.dataLoaded = true;
      } catch (err) {
        console.error(err);
        this.errorLoading = true;
      }
    },
    async getRCMeta(coordinatorUuid) {
      const query = await MIOService.getRCMeta(coordinatorUuid);
      this.rcMeta = query.data.coordinator[0];
      this.metaLoaded = true;
    }
  },
  computed: {
    ...mapGetters({
      getCoordinatorUuid: "auth/getCoordinatorUuid",
      isLoggedIn: "auth/isLoggedIn",
      isAdmin: "auth/isAdmin",
      isSRC: "auth/isSRC",
    }),
    coordinators: {
      get() {
        if (!this.$store.state.coordinators.coordinators) {
          return [];
        }
        let coordinators = {};
        this.$store.state.coordinators.coordinators.forEach(coord => {
          if (!coordinators[coord.country]) {
            coordinators[coord.country] = [];
          }
          coordinators[coord.country].push(coord);
        });
        return coordinators;
      }
    },
    RCTrainerActivityData: {
      get() {
        let teams = {},
          trainers = {},
          groups = {},
          observations = {},
          modules = {},
          dataConsenters = {},
          output = {
            cols: [
              {
                key: "trainer_name",
                label: "Trainer",
                class: "font-weight-bold",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "group_name",
                label: "Group name",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "group_type",
                label: "Group type",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "teacher_name",
                label: "Teacher",
                class: "font-weight-bold",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "school_name",
                label: "School",
                class: "font-weight-bold",
                sortable: true,
                thClass: "py-2"
              },
              { key: "modules", sortable: true, thClass: "py-2" },
              {
                key: "last_visit_date",
                label: "Last visit",
                class: "text-nowrap",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "last_visit_ppi",
                label: "Last PPI",
                sortable: true,
                thClass: "py-2"
              },
              {
                key: "average_ppi",
                label: "Average PPI",
                sortable: true,
                thClass: "py-2"
              }
            ],
            rows: [],
            data_warnings: {
              obs_field_missing: {
                label: "Visits missing key field data",
                data: []
              },
              obs_group_not_found: {
                label: "Group not found",
                data: []
              },
              type_mismatch: {
                label: "Observation and group types do not match",
                data: []
              }
            },
            location_warnings: {
              schools_not_geolocated: {
                label: "Schools not geolocated",
                data: []
              },
              obs_not_geolocated: {
                label: "Visits not geolocated",
                data: []
              },
              obs_school_mismatch: {
                label: "Visit/school location mismatches (>1km)",
                data: []
              }
            },
            missing_schools_warnings: {},
            counts: {
              teams: {},
              trainers: {},
              groups: {},
              teachers: {},
              schools: {}
            }
          },
          corners = [
            "work_displayed",
            "play_shop",
            "nature_box",
            "dress_up_box",
            "music",
            "arts",
            "reading_corner",
            "matching_games",
            "puzzles",
            "book_making",
            "conservation",
            "gardening"
          ],
          schoolsNotGeolocated = {};
        this.rcActiveTeamTrainers.forEach(trainer => {
          teams[trainer.team_uuid] = {};
          trainers[trainer.trainer_uuid] = {
            name: `${trainer.first_name} ${trainer.last_name}`
          };
        });
        this.rcActiveTeamTrainerGroups.forEach(group => {
          groups[group.group_uuid] = {
            name: group.group_name,
            type: group.type,
            trainer_uuid: group.trainer_uuid
          };
        });
        this.rcActiveTrainerTeacherModules.forEach(module => {
          module.teachers_uuid.forEach(teacher_uuid => {
            if (!modules[teacher_uuid]) {
              modules[teacher_uuid] = {};
            }
            modules[teacher_uuid][module.course] = true;
          });
        });
        this.dataConsenters.forEach(consenter => {
          dataConsenters[consenter.teacher_uuid] = consenter.date;
        });

        this.rcActiveTrainerRecentObservations.forEach(obs => {
          if (
            obs.trainer_uuid === "NULL" || !obs.trainer_uuid ||
            obs.teacher_uuid === "NULL" || !obs.teacher_uuid ||
            obs.school_uuid === "NULL" || !obs.school_uuid ||
            obs.group_uuid === "NULL" || !obs.group_uuid
          ) {
            output.data_warnings.obs_field_missing.data.push(
              `One of trainer_uuid: ${obs.trainer_uuid} | teacher_uuid: ${obs.teacher_uuid} | school_uuid: ${obs.school_uuid}`
            );
            return;
          }
          // Overall counts
          output.counts.trainers[obs.trainer_uuid] = {};
          output.counts.schools[obs.school_uuid] = {};
          output.counts.teachers[obs.teacher_uuid] = {};
          output.counts.groups[obs.group_uuid] = {};
          // Organise observations by classroom
          if (!observations[obs.trainer_uuid]) {
            observations[obs.trainer_uuid] = {};
          }
          if (!observations[obs.trainer_uuid][obs.teacher_uuid]) {
            observations[obs.trainer_uuid][obs.teacher_uuid] = {};
          }
          if (
            !observations[obs.trainer_uuid][obs.teacher_uuid][obs.school_uuid]
          ) {
            observations[obs.trainer_uuid][obs.teacher_uuid][obs.school_uuid] = {
              school_name: obs.school_name,
              school_lat: obs.school_lat,
              school_lng: obs.school_lng,
              teacher_name: `${obs.teacher_first_name} ${obs.teacher_last_name}`,
              group_uuid: obs.group_uuid,
              observations: []
            };
            if (!obs.school_lat || !obs.school_lng) {
              schoolsNotGeolocated[obs.school_uuid] = obs.school_name;
            }
          }
          let classroom =
            observations[obs.trainer_uuid][obs.teacher_uuid][obs.school_uuid];
          let dateObject = new Date(obs.observation_date);
          if (!groups[classroom.group_uuid]) {
            output.data_warnings.obs_group_not_found.data.push(
              `${trainers[obs.trainer_uuid]}, teacher: ${classroom.teacher_name}, school: ${classroom.school_name} has group_uuid ${classroom.group_uuid}, not found among this RC's active groups`
            );
          } else {
            classroom.group_type = groups[classroom.group_uuid].type;
          }
          if (obs.type != classroom.group_type) {
            output.data_warnings.type_mismatch.data.push(
              `${this.formatDate(dateObject)}: ${
                trainers[obs.trainer_uuid].name
              }, teacher: ${classroom.teacher_name}, school: ${
                classroom.school_name
              }, obs type: ${obs.type}, group type: ${classroom.group_type}`
            );
          }
          // Check location anomalies
          if (!obs.obs_lat && !obs.obs_lng) {
            output.location_warnings.obs_not_geolocated.data.push(
              `${this.formatDate(dateObject)}: ${
                trainers[obs.trainer_uuid].name
              }, teacher: ${classroom.teacher_name}, school: ${
                classroom.school_name
              }`
            );
          } else if (classroom.school_lat && classroom.school_lng) {
            let distance = calculateHaversine(
              [classroom.school_lat, classroom.school_lng],
              [obs.obs_lat, obs.obs_lng]
            );
            if (distance > 1) {
              output.location_warnings.obs_school_mismatch.data.push(
                `${this.formatDate(dateObject)}: ${
                  trainers[obs.trainer_uuid].name
                }, teacher: ${classroom.teacher_name}, school: ${
                  classroom.school_name
                } – visit appears to be ${distance.toFixed(1)}km from school`
              );
            }
          }
          // Teachers missing school warnings
          if (obs.missing_school) {
            output.missing_schools_warnings[obs.trainer_uuid] = output.missing_schools_warnings[obs.trainer_uuid] || {
              label: trainers[obs.trainer_uuid].name,
              teachers: {}
            };
            output.missing_schools_warnings[obs.trainer_uuid].teachers[obs.teacher_uuid] = output.missing_schools_warnings[obs.trainer_uuid].teachers[obs.teacher_uuid] || {
              label: classroom.teacher_name,
              obsCount: 0,
              missingConsent: !dataConsenters[obs.teacher_uuid],
            };
            output.missing_schools_warnings[obs.trainer_uuid].teachers[obs.teacher_uuid].obsCount++;
          }
          classroom.observations.push({
            date: dateObject,
            type: obs.type,
            corner_count: corners.reduce(
              (total, corner) => (obs[corner] ? ++total : total),
              0
            ),
            recommendation: obs.recommendation,
            coordinator_present: obs.coordinator_present,
            obs_lat: obs.obs_lat,
            obs_lng: obs.obs_lng,
            school_teacher_active: obs.school_teacher_active,
            teacher_delinked: obs.teacher_delinked,
          });
          // Calculate average PPI of 3 most recent visits
          let lastThreeVisits = classroom.observations.slice(0, 3);
          let visitsCount = 0,
            cornerTotal = 0;
          lastThreeVisits.forEach(visit => {
            cornerTotal += visit.corner_count;
            visitsCount++;
          });
          classroom.average_ppi =
            visitsCount > 0 ? cornerTotal / visitsCount : "—";
        });
        // Put schools missing coordinates into location warnings
        output.location_warnings.schools_not_geolocated.data = Object.keys(
          schoolsNotGeolocated
        ).map(uuid => `${schoolsNotGeolocated[uuid]}`);
        // Count all the totals
        Object.keys(output.counts).forEach(field => {
          output.counts[field] = Object.keys(output.counts[field]).length;
        });
        output.counts.teams = Object.keys(teams).length;
        // Assemble table
        Object.keys(observations).forEach(trainer_uuid => {
          Object.keys(observations[trainer_uuid]).forEach(teacher_uuid => {
            Object.keys(observations[trainer_uuid][teacher_uuid]).forEach(
              school_uuid => {
                let classroom =
                  observations[trainer_uuid][teacher_uuid][school_uuid];
                let group_name = "";
                if (classroom.group_type) {
                  group_name = groups[classroom.group_uuid].name;
                }
                let module_count = 0;
                if (modules[teacher_uuid]) {
                  module_count = Object.keys(modules[teacher_uuid]).length;
                }
                let data = {
                  trainer_name: trainers[trainer_uuid].name,
                  trainer_uuid: trainer_uuid,
                  group_name: group_name,
                  group_uuid: classroom.group_uuid,
                  group_type: classroom.group_type
                    ? groups[classroom.group_uuid].type.toLowerCase()
                    : "",
                  school_name: classroom.school_name,
                  school_uuid: school_uuid,
                  teacher_name: classroom.teacher_name,
                  teacher_uuid: teacher_uuid,
                  modules: module_count,
                  average_ppi: classroom.average_ppi.toFixed(1)
                };
                const lastVisit = classroom.observations[0]; // Relies on the query being ordered correctly
                if (lastVisit) {
                  data = {
                    ...data,
                    last_visit_date: lastVisit.date,
                    last_visit_warning: (
                        lastVisit.type.toLowerCase() == "maintenance"
                          ? isDateMonthsAgo(lastVisit.date, 6)
                          : isDateMonthsAgo(lastVisit.date)
                      // A teacher who left the programme is as un-chaseable as one who left
                      // the school, and the bonus feed already excludes them — but 4 in 5 of
                      // them are still school-attached, so they used to get the full badge.
                      ) ? (lastVisit.school_teacher_active && !lastVisit.teacher_delinked ? 'overdue' : 'inactive_overdue')
                      : false,
                    last_visit_type: lastVisit.type.toLowerCase(),
                    last_visit_coordinator_present:
                      lastVisit.coordinator_present,
                    last_visit_ppi: lastVisit.corner_count,
                    last_visit_lat: lastVisit.obs_lat,
                    last_visit_lng: lastVisit.obs_lng
                  };
                }
                output.rows.push(data);
              }
            );
          });
        });
        return output;
      }
    },
    hasDataWarnings() {
      if (!this.RCTrainerActivityData) {
        return false;
      }
      let warnings = this.RCTrainerActivityData.data_warnings;
      return Object.keys(warnings).reduce(
        (result, type) => result || warnings[type].data.length > 0,
        false
      );
    },
    hasLocationWarnings() {
      if (!this.RCTrainerActivityData) {
        return false;
      }
      let warnings = this.RCTrainerActivityData.location_warnings;
      return Object.keys(warnings).reduce(
        (result, type) => result || warnings[type].data.length > 0,
        false
      );
    },
    filteredItems() {
      let items = [];
      let filterValues = Object.keys(this.currentFilters).reduce(
        (ac, el) => ({ ...ac, [el]: {} }),
        {}
      );
      this.lastVisitWarningCount = 0;
      this.RCTrainerActivityData.rows.forEach(row => {
        // Firstly check if this row should be visible according to current filter choices
        let match = true;
        Object.keys(this.currentFilters).forEach(field => {
          if (!this.currentFilters[field]) {
            return;
          }
          let filterMatch = this.filterTypes.partial.includes(field)
            ? row[field]
                .toLowerCase()
                .trim()
                .includes(this.currentFilters[field].toLowerCase().trim())
            : row[field].toLowerCase().trim() ==
              this.currentFilters[field].toLowerCase().trim();
          match = filterMatch ? match && true : false;
        });
        // Push row if it matches
        if (match) {
          items.push(row);
          if (row.last_visit_warning) {
            this.lastVisitWarningCount++;
          }
        }
        // Add filter values
        Object.keys(this.currentFilters).forEach(field => {
          if (!row[field]) {
            return;
          }
          if (!filterValues[field][row[field]]) {
            filterValues[field][row[field]] = false;
          }
          // Always include values for the current row
          if (match) {
            filterValues[field][row[field]] = true;
          } else if (this.currentFilters[field]) {
            // If this filter is active, show any values from this field compatible with the other active filters
            let match = true;
            Object.keys(this.currentFilters).forEach(otherField => {
              if (!this.currentFilters[otherField] || field == otherField) {
                return;
              }
              let filterMatch = this.filterTypes.partial.includes(field)
                ? row[otherField]
                    .toLowerCase()
                    .trim()
                    .includes(
                      this.currentFilters[otherField].toLowerCase().trim()
                    )
                : row[otherField].toLowerCase().trim() ==
                  this.currentFilters[otherField].toLowerCase().trim();
              match = filterMatch ? match && true : false;
            });
            if (match) {
              filterValues[field][row[field]] = true;
            }
          }
        });
      });
      Object.keys(this.currentFilters).forEach(field => {
        if (this.currentFilters[field] != this.initialFilters[field]) {
          this.setUrlParam(field, this.currentFilters[field]);
        }
        this.availableChoices[field] = Object.keys(filterValues[field])
          .filter(key => filterValues[field][key])
          .sort();
      });
      return items;
    }
  }
};
</script>

<style>
@import "https://unpkg.com/leaflet@1.7.1/dist/leaflet.css";
.trainer-activity-map {
  height: 70vh;
}
.badge-warning.warning-light {
  background-color: #f19a3766;
}
.table.b-table > thead > tr > .table-b-table-default {
  background-color: inherit;
  color: inherit;
}
</style>